import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { SetFormActionWithId } from '@app/finxone-web-frontend/app/lib/actions/form-submission.action';
import { Country } from '@app/finxone-web-frontend/app/lib/dtos/countries.dto';
import { Currency } from '@app/finxone-web-frontend/app/lib/dtos/currencies.dto';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { CountriesCurrenciesService } from '@app/finxone-web-frontend/app/lib/services/countries-currencies-service/countries-currencies.service';
import { SavingsAccountService } from '@app/finxone-web-frontend/app/lib/services/savings-account-service/savings-account.service';
import { NominatedAccountDetailsResponse } from '@app/finxone-web-frontend/app/lib/services/savings-account-service/savings-account.type';
import { FormActionState } from '@app/finxone-web-frontend/app/lib/state/form-submision.state';
import { buildHandleBarModel } from '@app/finxone-web-frontend/app/lib/utils/template/model-builder.utils';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { IconCacheService } from '@finxone-platform/shared/services';
import {
  BaseWidgetProperties,
  ProviderClass,
  baseCssConfigStyle,
  commonContentStyleList,
  commonTextContentStyleList,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import Handlebars from 'handlebars';
import { Subject, finalize, takeUntil } from 'rxjs';
import { BaseComponent } from '../base/base.component';

@Component({
  // eslint-disable-next-line @angular-eslint/prefer-standalone
  standalone: false,
  selector: 'finxone-web-frontend-nominated-account-selection-widget',
  template: `
    <div class="component-wrapper d-block h-100 pointer-events-none" [ngStyle]="contentStyle">
      <div class="account-selection-wrapper">
        @for (account of accountsList; track account.accountId) {
        <div class="account-item d-flex align-items-start gap-2 mb-3" (click)="handleSelection(account)">
          <finxone-input class="pt-1" [customInputTemplate]="customRadioInput"></finxone-input>
          <ng-template #customRadioInput>
            <input
              type="radio"
              [name]="'radio-' + account.accountId"
              [id]="'radio-' + account.accountId"
              [checked]="isRadioChecked?.[account.accountId] ?? false"
              [ngStyle]="{ height: '18px', width: '18px' }"
            />
          </ng-template>

          <div [ngStyle]="textContentStyle" [innerHTML]="renderTemplate(account)"></div>
        </div>
        } @empty { @if (isLoading) {
        <div [ngStyle]="textContentStyle" [innerHTML]="renderLoadingTemplate()"></div>
        } @else {
        <div [ngStyle]="textContentStyle" [innerHTML]="renderNoRecordsTemplate()"></div>
        } }
      </div>
    </div>
  `,
})
export class NominatedAccountSelectionWidgetComponent extends BaseComponent implements OnInit {
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();

  public widgetProperties: BaseWidgetProperties;
  public textContentStyle: baseCssConfigStyle;
  public currencies: Currency[] = [];
  public countries: Country[] = [];
  public accountsList: NominatedAccountDetailsResponse[] = [];
  public isLoading = false;
  public isRadioChecked: Record<string, boolean> = {};
  public content = '';
  public noRecordsTemplate = '';
  public templateFunction: HandlebarsTemplateDelegate<any>;
  public noRecordsTemplateFunction: HandlebarsTemplateDelegate<any>;
  public loadingTemplateFunction: HandlebarsTemplateDelegate<any>;

  private baseHandleBarModel: any;
  public loadingTemplate: any;

  constructor(
    public dataIn: ProviderClass<any>,
    public store: Store,
    public configService: ConfigService,
    private countriesCurrenciesService: CountriesCurrenciesService,
    private iconCacheService: IconCacheService,
    private savingsAccountService: SavingsAccountService,
  ) {
    super();
  }

  ngOnInit(): void {
    if (this.dataIn?.attri) {
      this.widgetProperties = this.dataIn.attri.widgetProperties;
    }
    this.getWidgetCssConfig();
    this.isLoading = true;
    this.fetchAndSetAccountDetails();

    this.content = this.widgetProperties?.textContent;
    this.noRecordsTemplate = this.dataIn.attri?.['noRecordsTemplate'] ?? '';
    this.loadingTemplate = this.widgetProperties?.['loadingTextContent'] ?? '';
    this.templateFunction = Handlebars.compile(this.content);
    this.noRecordsTemplateFunction = Handlebars.compile(this.noRecordsTemplate);
    this.loadingTemplateFunction = Handlebars.compile(this.loadingTemplate);
    buildHandleBarModel(this.store, '', this.countriesCurrenciesService, this.iconCacheService)
      .pipe(takeUntil(this.destroy$))
      .subscribe((model) => {
        this.baseHandleBarModel = model;
      });
  }

  private fetchAndSetAccountDetails() {
    const nominatedAccounts: string[] =
      this.store.selectSnapshot(FormActionState.getFormActionStateWithId('NominatedAccounts'))?.formData
        ?.accountIds ?? [];

    this.savingsAccountService
      .fetchNominatedAccountDetails(nominatedAccounts)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          console.info('Finished getting accounts for nomination');
        }),
      )
      .subscribe({
        next: (response) => {
          this.accountsList = response;
          this.isLoading = false;
        },
        error: (err: HttpErrorResponse) => {
          this.isLoading = false;
          this.accountsList = [];
          // Only log if it's NOT a cancellation (status !== 0)
          if (err.status !== 0) {
            console.error('Error fetching accounts to nominate:', err);
          } else {
            console.log('Request was cancelled (navigation/destroy)', err);
          }
          throw err;
        },
      });
  }

  public getWidgetCssConfig() {
    this.contentStyle = this.configService.getSpecificBaseCssStyle(
      this.dataIn?.attri,
      commonContentStyleList,
    );

    this.textContentStyle = this.configService.getSpecificBaseCssStyle(
      this.dataIn?.attri,
      commonTextContentStyleList,
    );
  }

  public handleSelection(account: NominatedAccountDetailsResponse) {
    this.isRadioChecked = { [account.accountId]: true };
    this.store.dispatch(
      new SetFormActionWithId(
        { type: 'AccountSelection', formData: { accountId: account.accountId } },
        'AccountSelection',
      ),
    );
  }

  public renderTemplate(account: NominatedAccountDetailsResponse) {
    return this.templateFunction({
      ...this.baseHandleBarModel,
      accountIban: account.iban,
      accountCurrency: account.currency,
      accountOwnerName: account.ownerName,
      accountName: account.name,
      accountProduct: account.product,
      cashAccountType: account.cashAccountType,
      accountAddress: account.address,
    });
  }

  public renderNoRecordsTemplate() {
    return this.noRecordsTemplateFunction({
      ...this.baseHandleBarModel,
    });
  }

  public renderLoadingTemplate() {
    return this.loadingTemplateFunction({
      ...this.baseHandleBarModel,
    });
  }
}
